import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  CommunicationCountsContext,
  CommunicationCountsContextValue,
} from './CommunicationCountsContextDef';
import { CommunicationCounts, makeCommunicationService } from '../services/communicationService';
import { useAuth } from '../hooks/useAuth';
import { hasMenuCapability } from '../utils/menuCapability';

/**
 * Poll every 60s (mirrors NotificationContext's pattern); ticks are skipped while the tab is
 * hidden so a wall of background tabs doesn't multiply the load.
 */
const POLLING_INTERVAL_MS = 60_000;

/**
 * Sidebar/bottom-nav badge counts for the Communication pages (Requested bookings + New messages).
 *
 * Polling is gated per surface:
 *  - internal users need the STRICT `communication` capability (`hasMenuCapability`, NOT the
 *    fail-open `canAccessMenu`) — a Finance user or a not-yet-hydrated session never polls a
 *    403-bound endpoint;
 *  - Partner-boundary users poll the partner-scoped endpoint;
 *  - everyone else (External/agent portal, anonymous) is inert.
 *
 * State resets on logout (the provider wraps the router and never unmounts), and an unchanged
 * poll result skips setState so the layout tree doesn't re-render every 60s for nothing.
 */
export const CommunicationCountsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [counts, setCounts] = useState<CommunicationCounts | null>(null);
  const { isAuthenticated, isExternal, isPartner, isAdmin, isSuperAdmin, user } = useAuth();

  const canUseInternal =
    isAuthenticated &&
    !isExternal &&
    !isPartner &&
    hasMenuCapability('communication', {
      isAdmin,
      isSuperAdmin,
      allowedMenuIds: user?.allowedMenuIds,
    });
  const portal = canUseInternal ? 'internal' : isAuthenticated && isPartner ? 'partner' : null;

  const service = useMemo(() => (portal ? makeCommunicationService(portal) : null), [portal]);

  const refresh = useCallback(async () => {
    if (!service) return;
    try {
      const res = await service.getCounts();
      if (res.success && res.data) {
        const next = res.data;
        // Keep the SAME object when nothing changed — no 60s render-thrash at the layout root.
        setCounts((prev) =>
          prev &&
          prev.requestedBookings === next.requestedBookings &&
          prev.newMessages === next.newMessages &&
          (prev.newIntake ?? 0) === (next.newIntake ?? 0) &&
          (prev.newFiles ?? 0) === (next.newFiles ?? 0) &&
          (prev.newSignups ?? 0) === (next.newSignups ?? 0)
            ? prev
            : next,
        );
      }
    } catch {
      // Silent — polling failure must not disrupt the UI.
    }
  }, [service]);

  useEffect(() => {
    if (!service) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- refresh is async; state is set after an awaited API poll, not synchronously in the effect body
    refresh();
    const interval = setInterval(() => {
      if (!document.hidden) void refresh();
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [service, refresh]);

  // Reset on logout so a stale badge never bleeds into the next session (NotificationContext pattern).
  useEffect(() => {
    if (!isAuthenticated) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional logout reset: provider wraps the router and never unmounts, so state must be cleared when auth (external system) changes
      setCounts(null);
    }
  }, [isAuthenticated]);

  const value = useMemo<CommunicationCountsContextValue>(
    () => ({ counts, refresh }),
    [counts, refresh],
  );

  return (
    <CommunicationCountsContext.Provider value={value}>
      {children}
    </CommunicationCountsContext.Provider>
  );
};
